import ROLE from "models/role";
import { Navigate } from "react-router";
import { PEOPLE_TEAM_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import PeopleTeamDashboardPage from "pages/PeopleTeam/DashboardPage";
import WorkforcePage from "pages/PeopleTeam/WorkforcePage";
import { GroupsPage, GROUPS_MANAGEMENT_FEATURE_FLAG } from "features/Group";
import { TaskGridPage } from "features/PeopleTeamTaskGrid";
import { JourneyPreviewPage, JOURNEY_PREVIEWER_FEATURE_FLAG } from "features/JourneyPreview";
import CompanyInfoPage from "pages/PeopleTeam/CompanySettings/CompanyInfoPage";
import CompanyIntegrationsPage from "pages/PeopleTeam/CompanyIntegrationsPage";
import StatsPage from "pages/PeopleTeam/StatsPage";
import IssuePage from "pages/PeopleTeam/IssuePage";
import InvertedIssuesPage from "pages/PeopleTeam/InvertedIssuesPage";
import IntegrationPage from "pages/PeopleTeam/IntegrationPage";
import IntegrationSuccessPage from "pages/PeopleTeam/IntegrationSuccessPage";
import ActivityFeedPage from "pages/PeopleTeam/ActivityFeedPage";
import NotifiersPage from "pages/PeopleTeam/CompanySettings/NotificationsSettingsPage";
import EmailSettingsPage from "pages/PeopleTeam/CompanySettings/EmailSettingsPage";
import CalendarSettingsPage from "pages/PeopleTeam/CompanySettings/CalendarSettingsPage";
import SSOSettingsPage from "pages/PeopleTeam/CompanySettings/SSOSettingsPage";
import PeopleTeamSettingsPage from "pages/PeopleTeam/CompanySettings/PeopleTeamSettingsPage";
import TaskSettingsPage from "pages/PeopleTeam/CompanySettings/TaskSettingsPage";
import NewHireExperienceSettingsPage from "pages/PeopleTeam/CompanySettings/NewHireExperienceSettingsPage";
import EmailTemplatePage, { PrebuiltEmailTemplatePage } from "pages/PeopleTeam/Resources/Email/EmailTemplatePage";
import MessageTemplatePage, {
  PrebuiltMessageTemplatePage,
} from "pages/PeopleTeam/Resources/Message/MessageTemplatePage";
import TaskTemplatePage, { PrebuiltTaskTemplatePage } from "pages/PeopleTeam/Resources/Task/TaskTemplatePage";
import MeetingTemplatePage from "pages/PeopleTeam/Resources/Meeting/MeetingTemplatePage";
import CheckpointTemplatePage from "pages/PeopleTeam/Resources/Checkpoint/CheckpointTemplatePage";
import { CustomFormNewPage, CustomFormPreviewPage, CustomFormShowPage } from "features/Resources/CustomForm";
import TemplatesListPage from "pages/PeopleTeam/ListTemplatesPage";
import ShowPathPage from "pages/Shared/Paths/ShowPathPage";
import PrebuiltPathPage from "pages/Shared/PrebuiltPaths/ShowPrebuiltPathPage";
import { NudgesSettings } from "features/nudges";
import { Route } from "./types";

const PEOPLE_TEAM_ROUTES_PERMISSIONS = {
  permission: [ROLE.PEOPLE_TEAM],
};

const PEOPLE_TEAM_SHOW_PATH_ROUTE: Route = {
  path: SHARED_ROUTES.paths.show,
  title: "Show Path",
  component: <ShowPathPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

// The standalone paths page was folded into the Planning page's Paths tab.
const PEOPLE_TEAM_LIST_PATHS_ROUTE: Route = {
  path: SHARED_ROUTES.paths.index,
  title: "List Paths",
  component: <Navigate to={`${PEOPLE_TEAM_ROUTES.planning.index}/paths`} replace />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_JOURNEY_PREVIEW_ROUTE: Route = {
  path: SHARED_ROUTES.paths.preview,
  title: "Journey Preview",
  component: <JourneyPreviewPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
  featureFlag: JOURNEY_PREVIEWER_FEATURE_FLAG,
};

const PEOPLE_TEAM_DASHBOARD_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.dashboard,
  title: "People Team Dashboard",
  component: <PeopleTeamDashboardPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_ISSUES_ROUTE_NEW: Route = {
  path: PEOPLE_TEAM_ROUTES.issues.base,
  title: "Show Issues",
  component: <InvertedIssuesPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_ISSUES_BY_ACTION_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.issues.actions,
  title: "Show Issues By Action",
  component: <InvertedIssuesPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_SHOW_ISSUE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.issues.show,
  title: "Show Issue",
  component: <IssuePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_SHOW_SCHEDULED_ACTIONS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.activityFeed.index,
  title: "Show Activity Feed",
  component: <ActivityFeedPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_TEMPLATES_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.planning.index,
  title: "View Templates",
  component: <TemplatesListPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_TEMPLATES_TYPE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.planning.showType,
  title: "View Templates by Type",
  component: <TemplatesListPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_EMAIL_TEMPLATE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.emailTemplates.show,
  title: "Email Template",
  component: <EmailTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_MEETING_TEMPLATE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.meetingTemplates.show,
  title: "Meeting Template",
  component: <MeetingTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_MESSAGE_TEMPLATE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.messageTemplates.show,
  title: "Message Template",
  component: <MessageTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_TASK_TEMPLATE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.taskTemplates.show,
  title: "Task Template",
  component: <TaskTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_CHECKPOINT_TEMPLATE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.checkpointTemplates.show,
  title: "Checkpoint Template",
  component: <CheckpointTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_CUSTOM_FORM_NEW_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.customForms.new,
  title: "New Form",
  component: <CustomFormNewPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_CUSTOM_FORM_SHOW_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.customForms.show,
  title: "Form",
  component: <CustomFormShowPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_CUSTOM_FORM_PREVIEW_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.customForms.preview,
  title: "Form Preview",
  component: <CustomFormPreviewPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_STATS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.stats,
  title: "People Team Stats",
  component: <StatsPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_WORKFORCE_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.workforce,
  title: "People Team Workforce",
  component: <WorkforcePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_GROUPS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.groups,
  title: "Groups",
  component: <GroupsPage />,
  permission: [ROLE.PEOPLE_TEAM, ROLE.IT_ADMIN],
  featureFlag: GROUPS_MANAGEMENT_FEATURE_FLAG,
};

const PEOPLE_TEAM_TASK_GRID_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.taskGrid,
  title: "Task Grid",
  component: <TaskGridPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.settings,
  title: "Company Settings",
  component: <Navigate to={PEOPLE_TEAM_ROUTES.company.info} replace />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_COMPANY_INFO_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.info,
  title: "Company Info",
  component: <CompanyInfoPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_NOTIFICATION_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.notifiers,
  title: "Notifications Setup",
  component: <NotifiersPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_EMAIL_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.emailSettings,
  title: "Email Settings",
  component: <EmailSettingsPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_CALENDAR_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.calendarSettings,
  title: "Calendar Settings",
  component: <CalendarSettingsPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_SSO_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.ssoSettings,
  title: "SSO Settings",
  component: <SSOSettingsPage />,
  permission: [ROLE.PEOPLE_TEAM, ROLE.IT_ADMIN],
};

const PEOPLE_TEAM_PEOPLE_TEAM_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.peopleTeamSettings,
  title: "People Team Settings",
  component: <PeopleTeamSettingsPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_TASK_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.taskSettings,
  title: "Task Settings",
  component: <TaskSettingsPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_NEW_HIRE_EXPERIENCE_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.newHireExperienceSettings,
  title: "New Hire Experience",
  component: <NewHireExperienceSettingsPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_COMPANY_INTEGRATIONS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.integrations.index,
  title: "Integrations",
  component: <CompanyIntegrationsPage />,
  permission: [ROLE.PEOPLE_TEAM, ROLE.IT_ADMIN],
};

const PEOPLE_TEAM_COMPANY_INTEGRATION_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.integrations.show,
  title: "Integration Page",
  component: <IntegrationPage />,
  permission: [ROLE.PEOPLE_TEAM, ROLE.IT_ADMIN],
};

const INTEGRATION_SUCCESS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.integrations.success,
  title: "Integration Success",
  component: <IntegrationSuccessPage />,
  permission: [ROLE.PEOPLE_TEAM, ROLE.IT_ADMIN],
};

const PEOPLE_TEAM_PREBUILT_PATH_ROUTE: Route = {
  path: SHARED_ROUTES.prebuilt.paths.show,
  title: "Prebuilt Path",
  component: <PrebuiltPathPage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_PREBUILT_EMAIL_TEMPLATE_ROUTE: Route = {
  path: SHARED_ROUTES.prebuilt.emailTemplates.show,
  title: "Prebuilt Email Template",
  component: <PrebuiltEmailTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_PREBUILT_MESSAGE_TEMPLATE_ROUTE: Route = {
  path: SHARED_ROUTES.prebuilt.messageTemplates.show,
  title: "Prebuilt Message Template",
  component: <PrebuiltMessageTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_PREBUILT_TASK_TEMPLATE_ROUTE: Route = {
  path: SHARED_ROUTES.prebuilt.taskTemplates.show,
  title: "Prebuilt Task Template",
  component: <PrebuiltTaskTemplatePage />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
};

const PEOPLE_TEAM_NUDGES_SETTINGS_ROUTE: Route = {
  path: PEOPLE_TEAM_ROUTES.company.nudgesSettings,
  title: "Nudges Settings",
  component: <NudgesSettings />,
  ...PEOPLE_TEAM_ROUTES_PERMISSIONS,
  featureFlag: "nudges_system",
};

export default [
  INTEGRATION_SUCCESS_ROUTE,
  PEOPLE_TEAM_CALENDAR_SETTINGS_ROUTE,
  PEOPLE_TEAM_COMPANY_INFO_ROUTE,
  PEOPLE_TEAM_COMPANY_INTEGRATIONS_ROUTE,
  PEOPLE_TEAM_COMPANY_INTEGRATION_ROUTE,
  PEOPLE_TEAM_CHECKPOINT_TEMPLATE_ROUTE,
  PEOPLE_TEAM_DASHBOARD_ROUTE,
  PEOPLE_TEAM_EMAIL_SETTINGS_ROUTE,
  PEOPLE_TEAM_EMAIL_TEMPLATE_ROUTE,
  PEOPLE_TEAM_ISSUES_BY_ACTION_ROUTE,
  PEOPLE_TEAM_ISSUES_ROUTE_NEW,
  PEOPLE_TEAM_LIST_PATHS_ROUTE,
  PEOPLE_TEAM_MEETING_TEMPLATE_ROUTE,
  PEOPLE_TEAM_MESSAGE_TEMPLATE_ROUTE,
  PEOPLE_TEAM_NUDGES_SETTINGS_ROUTE,
  PEOPLE_TEAM_NOTIFICATION_SETTINGS_ROUTE,
  PEOPLE_TEAM_PREBUILT_EMAIL_TEMPLATE_ROUTE,
  PEOPLE_TEAM_PREBUILT_MESSAGE_TEMPLATE_ROUTE,
  PEOPLE_TEAM_PREBUILT_PATH_ROUTE,
  PEOPLE_TEAM_PREBUILT_TASK_TEMPLATE_ROUTE,
  PEOPLE_TEAM_SETTINGS_ROUTE,
  PEOPLE_TEAM_SHOW_ISSUE_ROUTE,
  PEOPLE_TEAM_SHOW_PATH_ROUTE,
  PEOPLE_TEAM_SHOW_SCHEDULED_ACTIONS_ROUTE,
  PEOPLE_TEAM_SSO_SETTINGS_ROUTE,
  PEOPLE_TEAM_PEOPLE_TEAM_SETTINGS_ROUTE,
  PEOPLE_TEAM_TASK_SETTINGS_ROUTE,
  PEOPLE_TEAM_NEW_HIRE_EXPERIENCE_SETTINGS_ROUTE,
  PEOPLE_TEAM_STATS_ROUTE,
  PEOPLE_TEAM_TASK_TEMPLATE_ROUTE,
  PEOPLE_TEAM_CUSTOM_FORM_NEW_ROUTE,
  PEOPLE_TEAM_CUSTOM_FORM_SHOW_ROUTE,
  PEOPLE_TEAM_CUSTOM_FORM_PREVIEW_ROUTE,
  PEOPLE_TEAM_TEMPLATES_ROUTE,
  PEOPLE_TEAM_TEMPLATES_TYPE_ROUTE,
  PEOPLE_TEAM_WORKFORCE_ROUTE,
  PEOPLE_TEAM_GROUPS_ROUTE,
  PEOPLE_TEAM_JOURNEY_PREVIEW_ROUTE,
  PEOPLE_TEAM_TASK_GRID_ROUTE,
];
